import { motion } from "framer-motion";
import { Boxes, Coins, Server, Zap, Globe2, Code2, ShieldCheck, Activity } from "lucide-react";

const FEATURES = [
    { icon: Code2, title: "EVM Compatible", body: "Deploy Solidity contracts using familiar Ethereum tooling — Hardhat, Foundry, MetaMask, Remix." },
    { icon: Server, title: "DePIN Native", body: "Designed for node registration, staking, uptime proofs, resource verification, and rewards." },
    { icon: Zap, title: "Fast Finality", body: "Built for infrastructure transactions with sub-3-second deterministic finality." },
    { icon: Coins, title: "Cloud Settlement Layer", body: "Settle compute, storage, bandwidth, GPU, DNS, and AI workload payments in $DCX." },
    { icon: ShieldCheck, title: "Proof Infrastructure", body: "Storage proofs, node proofs, workload records, and routing events anchored on-chain." },
    { icon: Boxes, title: "Developer Revenue Share", body: "dApp builders receive protocol-level revenue from activity generated by their applications." },
];

const EXPLORER_BLOCKS = [
    { h: "#9,184,302", txs: 1284, finality: "1.84s", status: "Finalized" },
    { h: "#9,184,301", txs: 1102, finality: "1.92s", status: "Finalized" },
    { h: "#9,184,300", txs: 1456, finality: "2.01s", status: "Finalized" },
    { h: "#9,184,299", txs: 982, finality: "1.78s", status: "Finalized" },
];

export default function Layer1() {
    return (
        <section id="layer1" className="relative py-24 md:py-32 lg:py-40 border-t border-line overflow-hidden" data-testid="layer1-section">
            <div className="absolute inset-0 hero-glow-bg opacity-40 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                <div className="max-w-3xl mb-16">
                    <div className="overline mb-4">03 — DeCloudX Layer-1</div>
                    <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.06] tracking-tight text-ink">
                        A Layer-1 Built for<br />
                        <span className="brand-gradient-text">Infrastructure, Not Speculation.</span>
                    </h2>
                    <p className="mt-6 text-base text-muted-ink max-w-2xl leading-relaxed">
                        DeCloudX Layer-1 is purpose-built to coordinate decentralized infrastructure. It verifies nodes, records workloads, settles compute and storage payments, powers smart contracts, manages DNS records, anchors storage proofs, distributes rewards, and enables developers to build cloud-native dApps directly on DeCloudX.
                    </p>
                </div>

                <div className="grid lg:grid-cols-12 gap-6 mb-16">
                    {/* Block explorer */}
                    <div className="lg:col-span-7 surface rounded-2xl overflow-hidden" data-testid="layer1-explorer">
                        <div className="flex items-center justify-between px-5 md:px-7 py-4 border-b border-line bg-card-muted">
                            <div className="flex items-center gap-3">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-ink">DeCloudX Mainnet · Live</span>
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">Block Explorer</div>
                        </div>

                        <div className="grid grid-cols-3 border-b border-line">
                            {[
                                { k: "Block Height", v: "9,184,302" },
                                { k: "Avg Finality", v: "1.89s" },
                                { k: "TPS (24h)", v: "3,842" },
                            ].map((m, i) => (
                                <div key={i} className="p-5 border-r border-line last:border-r-0">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-faint-ink">{m.k}</div>
                                    <div className="font-heading font-bold text-lg text-ink mt-1.5">{m.v}</div>
                                </div>
                            ))}
                        </div>

                        <div>
                            <div className="px-5 md:px-7 py-3 grid grid-cols-12 gap-3 font-mono text-[9px] uppercase tracking-[0.2em] text-faint-ink border-b border-line bg-card-muted">
                                <div className="col-span-4">Block</div>
                                <div className="col-span-3 text-right">Txns</div>
                                <div className="col-span-3 text-right">Finality</div>
                                <div className="col-span-2 text-right">Status</div>
                            </div>
                            {EXPLORER_BLOCKS.map((b, i) => (
                                <div key={i} className="px-5 md:px-7 py-3.5 grid grid-cols-12 gap-3 hover:bg-card-muted transition-colors items-center border-b border-line last:border-b-0" data-testid={`explorer-block-${i}`}>
                                    <div className="col-span-4 font-mono text-xs text-brand-blue">{b.h}</div>
                                    <div className="col-span-3 text-right font-mono text-xs text-ink">{b.txs}</div>
                                    <div className="col-span-3 text-right font-mono text-xs text-muted-ink">{b.finality}</div>
                                    <div className="col-span-2 text-right">
                                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 font-mono text-[9px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                                            <span className="w-1 h-1 rounded-full bg-emerald-500" />{b.status}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5 grid gap-4">
                        {[
                            { icon: Activity, k: "Consensus", v: "DPoS-BFT", sub: "Delegated PoS with BFT safety" },
                            { icon: Zap, k: "Throughput", v: "5,000+ TPS", sub: "Infrastructure-grade settlement" },
                            { icon: Code2, k: "Precompiles", v: "Storage · Compute · AI · DNS", sub: "DeCloudX-specific contracts" },
                        ].map((m, i) => (
                            <div key={i} className="surface card-lift p-6 rounded-2xl" data-testid={`layer1-metric-${i}`}>
                                <m.icon size={18} className="text-brand-cyan mb-3" strokeWidth={1.5} />
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">{m.k}</div>
                                <div className="font-heading font-bold text-xl text-ink mt-1">{m.v}</div>
                                <div className="text-xs text-muted-ink mt-1.5">{m.sub}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {FEATURES.map((f, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            className="surface card-lift p-7 rounded-2xl"
                            data-testid={`layer1-feature-${i}`}
                        >
                            <div className="w-10 h-10 rounded-xl bg-card-muted border border-line flex items-center justify-center mb-5">
                                <f.icon size={18} className="text-brand-blue" strokeWidth={1.5} />
                            </div>
                            <h3 className="font-heading font-bold text-base text-ink mb-2">{f.title}</h3>
                            <p className="text-sm text-muted-ink leading-relaxed">{f.body}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
